import { ThemeColor } from '@/enums'
import { useFormContext, useFormItem } from '@/providers'
import { fromEventTarget, themeColorFrom } from '@/utils'
import { Input } from '@surein/ui'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { prop } from 'styled-tools'
import { getError } from './helpers'
import { LucideIcon } from 'lucide-react'

export const StyledMessage = styled.p<{ color: ThemeColor }>`
  cursor: inherit;
  white-space: pre-line;
  line-height: 140%;
  font-weight: 700;
  font-size: 12px;
  color: ${themeColorFrom(prop('color'))};
`

interface Props {
  name: string
  label: string
  disabled?: boolean
  prefixIcon?: LucideIcon
  suffixIcon?: LucideIcon
  infoMessage?: string
  showErrorMessage?: boolean
  showValidCheck?: boolean
  validateOnChange?: boolean
  className?: string
  type?: 'number' | 'text'
}

export const FormInputField = ({
  name,
  label,
  disabled,
  prefixIcon,
  suffixIcon,
  infoMessage,
  showErrorMessage = true,
  showValidCheck = false,
  validateOnChange = false,
  className,
  ...props
}: Props) => {
  const { validateField } = useFormContext()
  const [touched, setIsTouched] = useState(false)
  const { value, onChange, error } = useFormItem(name)
  const errorMsg = getError(error) as string | null

  useEffect(() => {
    if (touched && validateOnChange && value) validateField(name)
  }, [validateOnChange, touched, value])

  const hasErrorMessage = !!(errorMsg && showErrorMessage)
  const isSuccessful = showValidCheck && touched && !error && value

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <Input
        disabled={disabled}
        id={name}
        isErrored={Boolean(error)}
        isSuccessful={isSuccessful}
        label={label}
        name={name}
        onBlur={() => touched && validateField(name)}
        onChange={fromEventTarget(onChange)}
        onFocus={() => setIsTouched(true)}
        prefixIcon={prefixIcon}
        suffixIcon={suffixIcon}
        value={value}
        className={className}
        {...props}
      />

      {hasErrorMessage && (
        <StyledMessage color={ThemeColor.danger2}>{errorMsg}</StyledMessage>
      )}

      {infoMessage && !hasErrorMessage && (
        <StyledMessage color={ThemeColor.b50}>{infoMessage}</StyledMessage>
      )}
    </div>
  )
}
